import Vue from 'vue'
import Vuex from 'vuex'
import moment from 'moment'
import utils from 'js/bankrate-utilities'
import { forEach, assign, some } from 'lodash'
import calculatorEventBus from 'apps/bankrate-calculators/events'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    isMobile: utils.isMobile(),
    loanAmount: 0,
    termInYears: 0,
    termInMonths: 0,
    interestRate: 0,
    startDate: moment().format('MM/DD/YYYY'),
    monthlyExtraPayment: 0,
    yearlyExtraPayment: 0,
    yearlyExtraPaymentMonth: moment()
      .add(1, 'M')
      .month(),
    oneTimeExtraPayment: 0,
    oneTimeExtraPaymentDate: moment().add(1, 'M'),
    monthlyPayment: 0,
    totalPrincipalPaid: 0,
    totalInterest: 0,
    estimatedPayoffDate: moment(),
    showAmortizationSchedule: false,
    extraPaymentsDivOpen: false,
    errors: {
      loanAmount: false,
      termInYears: false,
      termInMonths: false,
      interestRate: false,
      startDate: false,
      monthlyExtraPayment: false,
      yearlyExtraPayment: false,
      oneTimeExtraPayment: false,
      oneTimeExtraPaymentDate: false
    },
    previousUserInputs: {
      loanAmount: 0,
      termInMonths: 0,
      interestRate: 0,
      startDate: moment().format('MM/DD/YYYY'),
      monthlyExtraPayment: 0,
      yearlyExtraPayment: 0,
      yearlyExtraPaymentMonth: moment()
        .add(1, 'M')
        .month(),
      oneTimeExtraPayment: 0,
      oneTimeExtraPaymentDate: moment().add(1, 'M')
    }
  },
  mutations: {
    updatePageState(state, payload) {
      state[payload.name] = payload.value
    },
    updatePreviousUserInputState(state) {
      forEach(state.previousUserInputs, (value, key) => {
        state.previousUserInputs[key] = state[key]
      })
    },
    setPageErrors(state, payload) {
      const error = payload.error === undefined ? false : payload.error
      state.errors = assign(state.errors, { [payload.name]: error })
    }
  },
  actions: {
    calculatorLoaded({ state }, config) {
      forEach(state, (value, key) => {
        if (config[key] !== undefined && config[key].default) {
          state[key] = config[key].default
        }
      })
      state.estimatedPayoffDate = moment().add(config.termInMonths.default, 'M')
    },
    setState({ commit }, payload) {
      commit('updatePageState', { name: payload.name, value: payload.value })
      commit('setPageErrors', { name: payload.name, error: payload.error })
    },
    setNumberValue({ commit }, payload) {
      commit('updatePageState', { name: payload.name, value: payload.value })
      commit('setPageErrors', { name: payload.name, error: payload.error })
    },
    setDateValue({ commit }, payload) {
      commit('updatePageState', { name: payload.name, value: payload.value })
      commit('setPageErrors', { name: 'startDate', error: payload.error })
    },
    setTermInMonths({ commit }, payload) {
      commit('updatePageState', { name: 'termInMonths', value: payload.value })
      commit('updatePageState', {
        name: 'termInYears',
        value: isNaN(payload.value) ? 0 : payload.value / 12
      })
      commit('setPageErrors', { name: payload.name, error: payload.error })
    },
    setTermInYears({ commit }, payload) {
      commit('updatePageState', { name: 'termInYears', value: payload.value })
      commit('updatePageState', {
        name: 'termInMonths',
        value: isNaN(payload.value) ? 0 : payload.value * 12
      })
      commit('setPageErrors', { name: payload.name, error: payload.error })
    },
    setCurrencyValue({ commit }, payload) {
      const value = isNaN(payload.value)
        ? 0
        : Math.round(payload.value * 100) / 100
      commit('updatePageState', { name: payload.name, value: value })
      commit('setPageErrors', { name: payload.name, error: payload.error })
    },
    setMonth({ commit }, payload) {
      commit('updatePageState', {
        name: payload.name,
        value: moment(payload.value[0].name, 'MMMM').month()
      })
    },
    setOneTimeExtraPaymentMonth({ commit, state }, payload) {
      const newDate = state.oneTimeExtraPaymentDate.month(payload.value[0].name)
      commit('updatePageState', {
        name: 'oneTimeExtraPaymentDate',
        value: newDate
      })
      commit('setPageErrors', { name: 'oneTimeExtraPaymentDate', error: false })
    },
    setOneTimeExtraPaymentYear({ commit, state }, payload) {
      const newDate = state.oneTimeExtraPaymentDate.year(
        Number(payload.value[0].name)
      )
      commit('updatePageState', {
        name: 'oneTimeExtraPaymentDate',
        value: newDate
      })
      commit('setPageErrors', { name: 'oneTimeExtraPaymentDate', error: false })
    },
    updatePreviousUserInputs({ commit }) {
      commit('updatePreviousUserInputState')
    },
    toggleAmortizationSchedule({ state, dispatch }, payload) {
      state.showAmortizationSchedule = !state.showAmortizationSchedule
      dispatch('trackClick', payload)
    },
    trackClick(_context, payload) {
      // pass the text of link. For cases such as image links, pass the passed text.
      const fieldName = payload.text
        ? payload.text
        : payload.event.target.innerText.trim().toLowerCase()
      calculatorEventBus.$emit('calculator:engagement', fieldName)
    }
  },
  getters: {
    pageHasErrors(state) {
      return some(state.errors, item => {
        return item
      })
    },
    dataChanged(state) {
      return some(state.previousUserInputs, (input, key) => {
        return input !== state[key]
      })
    },
    loanAmount: state => state.loanAmount,
    termInYears: state => state.termInYears,
    termInMonths: state => state.termInMonths,
    interestRate: state => state.interestRate
  }
})
